import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Home, DollarSign, ShoppingCart, Package, FileText, TrendingUp, AlertTriangle, Users, FolderKanban } from 'lucide-react';
import AppHeader from '../components/AppHeader';

const SalesDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  
  const enconOrange = '#ee7203';
  const enconGrey = '#9d9d9c';

  const salesCards = [
    {
      id: 'projects',
      title: 'Projects',
      description: 'Track and manage building projects in your market sector',
      icon: FolderKanban,
      route: '/projects',
      comingSoon: false,
      requiresPermission: 'sales_dashboard_projects',
    },
    // Budget removed per GDPR requirements
    {
      id: 'price-support',
      title: 'Price Support System',
      description: 'Manage pricing support and adjustments',
      icon: DollarSign,
      comingSoon: true,
    },
    {
      id: 'b29-orders',
      title: 'B29 Orders',
      description: 'B29 order processing and tracking',
      icon: ShoppingCart,
      comingSoon: true,
    },
    {
      id: 'stock-transfers',
      title: 'Stock Transfers',
      description: 'Manage stock transfer operations',
      icon: Package,
      comingSoon: true,
    },
    {
      id: 'sales-plans',
      title: 'Sales Plans',
      description: 'Strategic sales planning tools',
      icon: TrendingUp,
      comingSoon: true,
    },
    {
      id: 'nil-spends',
      title: 'Nil Spends',
      description: 'Zero spend customer analysis',
      icon: AlertTriangle,
      comingSoon: true,
    },
    {
      id: 'exposure-analysis',
      title: 'Exposure Analysis',
      description: 'Financial exposure and risk analysis',
      icon: AlertTriangle,
      comingSoon: true,
    },
    {
      id: 'supplier-development',
      title: 'Supplier Development Plans',
      description: 'Supplier relationship development',
      icon: Users,
      comingSoon: true,
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#ffffff' }}>
      {/* Global Header with Search */}
      <AppHeader
        user={user}
        onLogout={onLogout}
        subHeaderLeft={
          <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
            <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">Sales Dashboard</span>
          </div>
        }
        subHeaderRight={
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/90 hover:bg-white transition-colors"
            style={{ color: enconOrange }}
            title="Home"
            data-testid="home-button"
          >
            <Home className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Home</span>
          </button>
        }
      />
      
      {/* Main Content */}
      <div className="container mx-auto px-4 md:px-6 py-6" style={{ marginTop: '36px' }}>
        {/* Sales Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sales-cards-grid">
          {salesCards.map((card) => {
            const IconComponent = card.icon;
            // Check permission - special handling for sales_dashboard_projects (default true) and sales_dashboard_budgets (super_admin by default)
            let isAvailable = !card.comingSoon;
            if (card.requiresPermission) {
              if (card.requiresPermission === 'sales_dashboard_projects') {
                // Projects is ON by default for everyone (unless explicitly set to false)
                isAvailable = isAvailable && (user?.sales_dashboard_projects !== false || user?.role === 'super_admin');
              } else {
                isAvailable = isAvailable && (user?.[card.requiresPermission] || user?.role === 'super_admin');
              }
            }
            
            return (
              <Card 
                key={card.id}
                data-testid={`sales-card-${card.id}`}
                className={`hover:shadow-lg transition-all duration-300 border-2 bg-white ${!isAvailable ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:scale-105'}`}
                style={{ borderColor: isAvailable ? enconOrange : enconGrey }}
                onClick={() => isAvailable && card.route && navigate(card.route)}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg md:text-xl font-bold" style={{ color: isAvailable ? enconOrange : enconGrey }}>
                      {card.title}
                    </CardTitle>
                    <IconComponent className="w-8 h-8 md:w-10 md:h-10" style={{ color: isAvailable ? enconOrange : enconGrey }} />
                  </div>
                  <CardDescription className="text-sm md:text-base">
                    {card.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!isAvailable && (
                  <div className="text-center py-4">
                    <span className="text-sm font-semibold px-4 py-2 rounded-lg" style={{ backgroundColor: '#f3f4f6', color: enconGrey }}>
                      Coming Soon
                    </span>
                  </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SalesDashboard;
